import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getLeaveDashboardSummary,
  getTodayOnLeave,
  getDepartmentCapacity,
  getDensityMap,
  getUpcomingTimeline,
  getApprovalPipeline,
  getBalanceDistribution,
  getBurnoutRisk,
  getRiskSignals,
} from '../../../services/leaveDashboardService';
import { getDepartments } from '../../../services/departmentService';
import { getLocations } from '../../../services/locationService';

const SLICES = {
  summary: getLeaveDashboardSummary,
  today: getTodayOnLeave,
  capacity: getDepartmentCapacity,
  density: getDensityMap,
  timeline: getUpcomingTimeline,
  pipeline: getApprovalPipeline,
  balance: getBalanceDistribution,
  burnout: getBurnoutRisk,
  risks: getRiskSignals,
};

const initialState = Object.keys(SLICES).reduce((acc, key) => {
  acc[key] = { data: null, loading: true };
  return acc;
}, {});

export const useLeaveDashboardApi = () => {
  const [filters, setFilters] = useState({ departmentId: null, locationId: null });
  const [slices, setSlices] = useState(initialState);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);

  const load = useCallback(async (activeFilters) => {
    setSlices((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => { next[key] = { ...next[key], loading: true }; });
      return next;
    });

    await Promise.all(Object.entries(SLICES).map(async ([key, fetcher]) => {
      try {
        const res = await fetcher(activeFilters);
        setSlices((prev) => ({ ...prev, [key]: { data: res.data?.data ?? null, loading: false } }));
      } catch {
        setSlices((prev) => ({ ...prev, [key]: { data: null, loading: false } }));
      }
    }));
  }, []);

  useEffect(() => {
    load(filters);
  }, [filters, load]);

  // Filter sources — first page is plenty for dropdowns.
  useEffect(() => {
    (async () => {
      try {
        const res = await getDepartments({ pageSize: 200, orderBy: 'name' });
        setDepartments(res.data?.data?.data ?? []);
      } catch { /* handled by interceptor */ }
      try {
        const res = await getLocations({ pageSize: 200, orderBy: 'name' });
        setLocations(res.data?.data?.data ?? []);
      } catch { /* handled by interceptor */ }
    })();
  }, []);

  const refetchAll = useCallback(() => load(filters), [load, filters]);

  // Every leave variation visible in the current payloads — drives the stable
  // categorical color map.
  const variations = useMemo(() => {
    const seen = new Map();
    (slices.today.data?.items || []).forEach((i) => seen.set(i.leaveVariationId, { id: i.leaveVariationId, name: i.leaveVariationName }));
    (slices.timeline.data?.items || []).forEach((i) => seen.set(i.leaveVariationId, { id: i.leaveVariationId, name: i.leaveVariationName }));
    return [...seen.values()];
  }, [slices.today.data, slices.timeline.data]);

  return {
    filters,
    setFilters,
    slices,
    departments,
    locations,
    variations,
    refetchAll,
  };
};
